/**
 * StageDetailModal — modal popup for a specific pipeline stage.
 *
 * Shows:
 * - Stage description from the manifest
 * - Per-pair list filtered to pairs that ran this stage, with verdict/score chips
 * - Click a pair row → opens PipelineDetailModal for the full pair view
 */
import { useState } from 'react';
import { X } from 'lucide-react';
import { clsx } from 'clsx';
import type { CreativeReviewPipelineState, PipelineStatus, StageId } from '@/services/api';
import { STAGE_MAP } from '../pipelineManifest';
import { PipelineStageChip } from './PipelineStageChip';
import { PipelineDetailModal } from './PipelineDetailModal';

interface Props {
  stageId: StageId;
  pipelines: CreativeReviewPipelineState[];
  onClose: () => void;
}

function VerdictChip({ verdict }: { verdict?: string | null }) {
  if (!verdict) return <span className="text-slate-400">—</span>;
  const color =
    verdict === 'strong' ? 'text-green-700 bg-green-50 border-green-200' :
    verdict === 'weak' ? 'text-red-700 bg-red-50 border-red-200' :
    'text-amber-700 bg-amber-50 border-amber-200';
  return (
    <span className={clsx('text-xs px-1.5 py-0.5 rounded-full border font-medium', color)}>
      {verdict}
    </span>
  );
}

export function StageDetailModal({ stageId, pipelines, onClose }: Props) {
  const [selectedPair, setSelectedPair] = useState<CreativeReviewPipelineState | null>(null);
  const entry = STAGE_MAP[stageId];

  const pairsWithStage = pipelines.filter(p => p.stages?.[stageId]);

  const statusOrder: Record<string, number> = {
    failed: 0, running: 1, completed: 2, skipped: 3, pending: 4, blocked: 5,
  };
  const sorted = [...pairsWithStage].sort((a, b) => {
    const sa = a.stages?.[stageId]?.status ?? 'pending';
    const sb = b.stages?.[stageId]?.status ?? 'pending';
    return (statusOrder[sa] ?? 9) - (statusOrder[sb] ?? 9);
  });

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
        <div
          className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start justify-between px-5 pt-5 pb-3 border-b border-slate-100">
            <div>
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-0.5">
                {entry?.phase_id ?? ''}
              </div>
              <h2 className="text-lg font-semibold text-slate-900">
                {entry?.label ?? stageId}
              </h2>
              {entry?.description && (
                <p className="text-sm text-slate-500 mt-1">{entry.description}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="ml-4 p-1 rounded-lg hover:bg-slate-100 text-slate-400"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Summary row */}
          {pipelines.length > 0 && (
            <div className="px-5 py-2 border-b border-slate-100 flex gap-4 text-xs text-slate-600">
              {(['completed', 'failed', 'skipped', 'running', 'pending'] as PipelineStatus[]).map(st => {
                const count = pairsWithStage.filter(
                  p => (p.stages?.[stageId]?.status ?? 'pending') === st,
                ).length;
                if (count === 0) return null;
                return (
                  <span key={st} className="flex items-center gap-1">
                    <PipelineStageChip label={`${count}`} status={st} size="xs" />
                    <span>{st}</span>
                  </span>
                );
              })}
              {pairsWithStage.length === 0 && (
                <span className="text-slate-400">No pairs processed yet</span>
              )}
            </div>
          )}

          {/* Pair list */}
          <div className="overflow-y-auto flex-1 px-5 py-3">
            {sorted.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center">
                No pair data available for this stage yet.
              </p>
            ) : (
              <div className="space-y-1">
                {sorted.map((p) => {
                  const stageState = p.stages?.[stageId];
                  const status = (stageState?.status ?? 'pending') as PipelineStatus;
                  const pairKey = `${p.creative_id}__${p.persona_id}`;
                  return (
                    <button
                      key={pairKey}
                      type="button"
                      onClick={() => setSelectedPair(p)}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg border border-slate-100 hover:bg-slate-50 text-left transition-colors"
                    >
                      <PipelineStageChip label={status} status={status} size="xs" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-mono truncate text-slate-700">
                          {p.creative_id}
                        </div>
                        <div className="text-xs text-slate-500 truncate">{p.persona_id}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <VerdictChip verdict={p.rollup?.verdict} />
                        {p.rollup?.overall_score !== null && p.rollup?.overall_score !== undefined && (
                          <span className="text-xs text-slate-500">
                            {p.rollup.overall_score.toFixed(1)}
                          </span>
                        )}
                        {stageState?.summary && (
                          <span className="text-xs text-slate-400 max-w-[120px] truncate">
                            {stageState.summary}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedPair && (
        <PipelineDetailModal
          pipeline={selectedPair}
          onClose={() => setSelectedPair(null)}
        />
      )}
    </>
  );
}
